import { useEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";

export type DropdownOption<T = string> = {
  value: T;
  label: string;
  swatch?: string;
};

type DropdownProps<T extends string = string> = {
  label?: string;
  value: T;
  onChange: (value: T) => void;
  options: DropdownOption<T>[];
  width?: number;
  align?: "left" | "right";
  /** "up" opens the menu above the trigger — use for pagers / bottom-of-page
   * dropdowns where a downward menu would clip off-screen. */
  direction?: "down" | "up";
};

/**
 * Controlled dropdown with click-outside to close. Used for single-value
 * selects in toolbars (period, placement, etc). Multi-select has its
 * own component.
 */
export function Dropdown<T extends string = string>({
  label,
  value,
  onChange,
  options,
  width,
  align = "left",
  direction = "down",
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  useLayer(open, () => setOpen(false));

  const current = options.find((o) => o.value === value) ?? options[0];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="dd-trigger"
        onClick={() => setOpen((o) => !o)}
        style={width ? { minWidth: width } : undefined}
      >
        {label && <span className="dd-label">{label}</span>}
        <span>{current?.label}</span>
        <span style={{ marginLeft: 4, color: "var(--c-text-3)" }}>{I.chevronDown}</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            ...(direction === "up"
              ? { bottom: "calc(100% + 4px)" }
              : { top: "calc(100% + 4px)" }),
            [align]: 0,
            zIndex: 20,
            background: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            borderRadius: 10,
            boxShadow: "var(--sh-3)",
            padding: 6,
            minWidth: 200,
          } as React.CSSProperties}
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <div
                key={o.value}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: active ? "var(--c-accent-50)" : "transparent",
                  color: active ? "var(--c-accent-700)" : "var(--c-text)",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "var(--c-surface-sunk)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                {o.swatch && (
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: o.swatch,
                    }}
                  />
                )}
                <span style={{ flex: 1 }}>{o.label}</span>
                {active && <span style={{ color: "var(--c-accent-600)" }}>{I.check}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
